import {
  Button,
  Flex,
  Heading,
  Progress,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Countdown from "react-countdown";
import { useUpdateEffect } from "react-use";

export function InitialPendingState({
  totalDuration,
  checkoutDateTime,
  onCancel,
  onTimerComplete,
}: {
  totalDuration: number;
  checkoutDateTime: string;
  onCancel: () => void;
  onTimerComplete: () => void;
}) {
  const { t } = useTranslation();
  const [countdownEnd, setCountdownEnd] = useState<Date | null>(null);

  useUpdateEffect(() => {
    if (totalDuration) {
      const endDate = new Date(
        new Date(checkoutDateTime).getTime() + totalDuration * 1000
      );
      setCountdownEnd(endDate);
    }
  }, [totalDuration]);

  return (
    <div className="p-8 h-full">
      <div className="flex flex-col items-center gap-6 h-full relative">
        <Flex className="absolute top-4 right-4">
          <Tooltip content={t("cancelCheckout")}>
            <Button color="red" size="3" variant="soft" onClick={onCancel}>
              {t("cancelCheckout")}
            </Button>
          </Tooltip>
        </Flex>
        <div className="w-full bg-white rounded-2xl border border-card-border p-6 grow">
          <div className="flex flex-col justify-center items-center h-full">
            <figure className="mb-6">
              <img
                src="/autonomous-pending.png"
                className="object-contain max-w-[450px]"
              />
            </figure>
            <Heading
              size="5"
              weight="bold"
              mb="3"
              align="center"
              color="gray"
              highContrast
              className="animate-text-gradient bg-gradient-to-r from-[--indigo-9] via-[--indigo-12] to-[--indigo-8] bg-[200%_auto] bg-clip-text !text-transparent"
            >
              Our system is processing your interactions in the store, and
              you'll see it in action soon!
            </Heading>

            {countdownEnd ? (
              <Countdown
                autoStart
                date={countdownEnd}
                onComplete={onTimerComplete}
                renderer={({ seconds, minutes, total }) => (
                  <div className="w-[270px] space-y-1">
                    <Progress
                      value={(1 - total / (totalDuration * 1000)) * 100}
                      duration="20s"
                      className="[&_.rt-ProgressIndicator]:duration-1000"
                    />
                    <Flex justify="between">
                      <Text color="gray" size="2">
                        Retrieving customer information...
                      </Text>
                      <Text color="gray" size="2" className="tabular-nums">
                        {String(minutes).padStart(2, "0")}:
                        {String(seconds).padStart(2, "0")}
                      </Text>
                    </Flex>
                  </div>
                )}
              ></Countdown>
            ) : (
              <div className="w-[270px] space-y-1">
                <Progress
                  value={0}
                  duration="20s"
                  className="[&_.rt-ProgressIndicator]:duration-1000"
                />
                <Flex justify="between">
                  <Text color="gray" size="2">
                    Retrieving customer information...
                  </Text>
                  <Text color="gray" size="2" className="tabular-nums">
                    {String(0).padStart(2, "0")}:{String(0).padStart(2, "0")}
                  </Text>
                </Flex>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
